<template>
  <div ref="lyricMaker" class="lyric-maker" @keydown="handlePress" tabIndex="1">
    <el-steps :active="step" finish-status="success" simple>
      <el-step title="录入歌词"></el-step>
      <el-step title="制作歌词"></el-step>
      <el-step title="预览"></el-step>
    </el-steps>

    <div v-if="step === 1">
      <player key="1" :howler-data="howlerData" :type="media.fileType"></player>
      <el-row>
        <el-col :span="24" class="info-block">
          <el-form :model="lyricObj.baseInfo" :inline-message="true" :show-message="false" label-width="70px">
            <el-form-item label="音译歌词">
              <el-checkbox v-model="needRoma" :true-label="1" :false-label="0"></el-checkbox>
              <el-select v-show="needRoma" v-model="romaType" placeholder="选择歌词语种" clearable>
                <el-option v-for="(name, key) in romaTypeMap" :key="key" :label="name" :value="+key"></el-option>
              </el-select>
            </el-form-item>
          </el-form>
        </el-col>
        <el-col :span="24" class="info-block">
          <el-input type="textarea" :rows="15" v-model="originLyricText"></el-input>
        </el-col>
      </el-row>
    </div>

    <div v-if="step === 2">
      <img src="@/assets/keyboard.png" alt="指示">
      <player
        key="2"
        ref="player"
        :howler-data="howlerData"
        @position-change="currentPlayPosition"
        @slider-change="currentSliderChange"
        :type="media.fileType"
      ></player>
      <div class="maker-wrapper">
        <div class="info-panel">
          <span>行：{{ currentMarkLine + 1 }}</span>&nbsp;&nbsp;
          <span>列：{{ currentMarkWord + 1 }}</span>
        </div>
        <div class="content" :style="{top: contentTop, left: contentLeft}">
          <div
            v-for="(sentence, lineIndex) in lyricAST.ASTList"
            :key="lineIndex"
            :class="{line: true, marked: lineIndex < currentMarkLine, 'line__current': currentMarkLine === lineIndex}"
          >
            <div class="text">
              <span
                v-for="(wordObj, wordIndex) in sentence"
                :id="`${lineIndex}@@${wordIndex}`"
                :key="wordObj._id"
                :class="{word: true,
                         marked: (lineIndex <= currentMarkLine && wordIndex <= currentMarkWord),
                         current: lineIndex === currentMarkLine && currentMarkWord === wordIndex && !alreadyCal}"
              >
                {{ wordObj.word }}
                <span
                  v-if="needRoma"
                  class="tls-word"
                >{{ lyricAST.tlsAST.ASTList[lineIndex][wordIndex].word }}</span>
              </span>
              <span v-if="lineCount === (lineIndex + 1) && makeFinish" class="finish">finish!</span>
            </div>
            <div v-if="currentMarkLine === lineIndex" class="tool" :style="{left: toolRight}">
              <el-button type="info" @click="handleLineModify(lineIndex)">修改</el-button>
              <el-button v-if="needRoma" type="info" @click="handleTlsLineModify(lineIndex)">修改音译</el-button>
              <el-button type="danger" @click="handleLineDelete(lineIndex)">删除</el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="step === 3">
      <player
        key="3"
        ref="player"
        :howler-data="howlerData"
        @position-change="previewPlayPostion"
        @slider-change="currentSliderChange"
        :type="media.fileType"
      ></player>
      <div class="maker-wrapper">
        <div class="info-panel">
          <span>行：{{ currentMarkLine + 1 }}</span>&nbsp;&nbsp;
          <span>列：{{ currentMarkWord + 1 }}</span>
        </div>
        <div class="content play-mod" :style="{top: contentTop}">
          <div
            v-for="(sentence, lineIndex) in lyricAST.ASTList"
            :key="lineIndex"
            :class="{line: true, marked: lineIndex < currentMarkLine, 'line__current': currentMarkLine === lineIndex}"
          >
            <div class="text">
              <span
                v-for="(wordObj, wordIndex) in sentence"
                :key="wordObj._id"
                :class="{
                  word: true,
                  marked: (lineIndex <= currentMarkLine && wordIndex <= currentMarkWord),
                  current: currentMarkWord <= wordIndex}"
              >
                {{ wordObj.word }}
                <span
                  v-if="needRoma"
                  class="tls-word"
                >{{ lyricAST.tlsAST.ASTList[lineIndex][wordIndex].word }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="center btn">
      <el-button v-if="step > 1" type="primary" @click="handlePrev">上一步</el-button>
      <el-button v-if="step < 3" type="primary" @click="handleNext">下一步</el-button>
      <el-button v-if="step === 3" type="success" @click="lyricMakeFinish">提交</el-button>
    </div>

    <el-dialog
      v-if="showRomaEdit"
      title="编辑音译"
      :visible.sync="showRomaEdit"
      :append-to-body="true"
      :close-on-click-modal="false"
      @close="handleCancelTlsEdit"
    >
      <div class="tls-edit-line" id="tls-edit-block">
        <div
          v-for="(text, index) in originTextList"
          :key="`${text}${index}`"
          class="tls-edit-word"
          contenteditable
          @input="handleTlsInput(index, $event)"
        >
          {{ tlsTextList[index] }}
          <span class="tls-edit-orig" contenteditable="false">{{ text }}</span>
        </div>
      </div>
      <template #footer>
        <el-button type="ghost" @click="handleCancelTlsEdit">取 消</el-button>
        <el-button type="primary" @click="handleTlsConfirm">确 认</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import cloneDeep from 'lodash-es/cloneDeep'
import commonService from '@/api/common'
import Player from '@/components/Player'
import { japaneseTls, cantoneseTls, koreanTls } from '@/utils/transliteration/index.js'
import LyricAST from '../lyric/LyricAST'
import Trc from '../lyric/Trc.js'
import TrcRoma from '../lyric/TrcRoma.js'
import Krc from '../lyric/Krc.js'
import KrcRoma from '../lyric/KrcRoma.js'
import Lrc from '../lyric/Lrc.js'
import LrcRoma from '../lyric/LrcRoma.js'
import Lrcx from '../lyric/Lrcx.js'
import LrcxRoma from '../lyric/LrcxRoma.js'

const KEY_SPACE = 'Space'
const KEY_RIGHT = 'ArrowRight'
const KEY_LEFT = 'ArrowLeft'
const KEY_UP = 'ArrowUp'
const KEY_DOWN = 'ArrowDown'

export default {
  name: 'LyricMaker',
  components: {
    Player
  },
  props: {
    track: {
      type: Object,
      required: true
    },
    media: {
      type: Object,
      required: true
    },
    txtLyricUrl: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      howlerData: {},
      originLyricText: '',
      step: 1,
      lyricObj: {
        baseInfo: {
          mediaMd5: '',
          trackName: '',
          singerName: '',
          albumName: '',

        }
      },
      romaTypeMap: {
        1: '韩文',
        2: '日文',
        3: '粤语'
      },
      romaTlsMap: Object.freeze({
        1: koreanTls,
        2: japaneseTls,
        3: cantoneseTls
      }),
      lyricAST: {},
      currentMarkLine: 0,
      currentMarkWord: 0,
      currentLineLength: 0,
      lineCount: 0,
      contentTop: 0,
      contentLeft: 0,
      maxViewWidth: 560,
      toolRight: '560px',
      makeFinish: false,
      alreadyCal: false, // 表示当前字是否通过“停顿”按钮计算时间，按 up down left 都需要恢复 false
      needRoma: false,
      romaType: '',
      showRomaEdit: false,
      originTextList: [],
      tlsTextList: [],
      editTlsLine: 0,
    }
  },
  computed: {
    copyrightText() {
      return {
        normal: '（版权所有未经许可，请勿使用）',
        trans: '腾讯享有本翻译作品的著作权'
      }
    }
  },
  watch: {
    currentMarkLine: {
      immediate: true,
      handler(newVal) {
        // 使当前行保持在中间
        if (newVal > 5) {
          this.contentTop = `-${(newVal - 5) * 46}px`
        } else {
          this.contentTop = '0px'
        }
      }
    },
    currentMarkWord: {
      immediate: true,
      handler(currentWord) {
        const currentLine = this.currentMarkLine
        const wordElm = document.getElementById(`${currentLine}@@${currentWord}`)
        if (!wordElm) {
          return
        }
        const elmRightPos = wordElm.offsetLeft + wordElm.offsetWidth

        // console.log(wordElm.offsetLeft, wordElm.offsetWidth)
        // console.log(elmRightPos, this.maxViewWidth, elmRightPos - this.maxViewWidth)
        let cLeft = 0
        if (elmRightPos > this.maxViewWidth) {
          cLeft = elmRightPos - this.maxViewWidth
          this.contentLeft = `-${cLeft}px`
        } else {
          this.contentLeft = '0px'
        }
        this.toolRight = `${this.maxViewWidth + cLeft}px`
      }
    }
  },
  created() {
    this.initLyricObj()
    this.initPlayer()
    this.getLyricText()
  },
  currentPos: 0,
  methods: {
    getLyricText() {
      commonService.getLyricText(this.txtLyricUrl).then(resp => {
        this.originLyricText = resp.data.trim()
      }).catch(error => {
        this.$message({
          type: 'error',
          message: '获取文本歌词异常: ' + error.message
        })
      })
    },
    initPlayer() {
      const { media } = this

      this.howlerData = {
        src: [media.mediaUrl]
      }
    },

    initLyricObj() {
      const { lyricObj, track } = this
      const baseInfo = lyricObj.baseInfo
      baseInfo.trackId = track.trackId
      baseInfo.trackName = track.trackName
      baseInfo.singerName = track.trackSingerList.map(item => item.singerName).join(',')
      baseInfo.author = this.$store.state.user.enName

      if (track.album) {
        baseInfo.albumName = track.album.split(' -@- ')[0]
      }
    },

    /**
     * 解析文本歌词，生成歌词 AST 对象
     */
    parseLyricData() {
      const { originLyricText, lyricObj, needRoma, romaType, romaTlsMap } = this
      const lyricAST = new LyricAST(originLyricText, lyricObj.baseInfo, { autoAST: true })

      this.lyricAST = lyricAST
      if (needRoma) {
        const translator = romaTlsMap[romaType]
        const ast = cloneDeep(lyricAST)
        ast.transliterate(translator)
        lyricAST.tlsAST = ast
      }

      this.lineCount = lyricAST.getASTLength()
      this.currentLineLength = lyricAST.getLineLength(0) // 获取第一行的长度
    },

    handlePrev() {
      switch (this.step) {
        case 2:
        case 3:
          this.reset()
          break
        default:
      }
      this.step = this.step - 1
    },

    handleNext() {
      const { step, makeFinish, needRoma, romaType } = this
      switch (step) {
        case 1:
          if (needRoma) {
            if (!romaType) {
              return this.$alert('请选择歌词语种', '提示', {
                type: 'warning'
              })
            }

            // 音译的多一个按钮，所以调整左边距离
            this.maxViewWidth = 490
            this.toolRight = '490px'
          } else {
            this.maxViewWidth = 560
            this.toolRight = '560px'
          }
          this.parseLyricData()
          this.step = 2
          break
        case 2:
          if (!makeFinish) {
            return this.$alert('歌词还未制作完成', '提示', {
              type: 'warning'
            })
          }
          if (!this.validate()) {
            return
          }

          this.reset()
          this.step = 3
          break
        default:
          break
      }
    },

    validate() {
      const { lyricAST } = this

      /**
       * 验证规则：
       * 1、下一行开始时间 >= 上一行开始时间 + 持续时间
       * 2、下一个字开始时间 >= 上一个字开始时间 + 持续时间
       * 3、行、字开始时间不能为 0
       */

      for (let i = 0; i < lyricAST.getASTLength(); i++) {
        const lineLen = lyricAST.getLineLength(i)

        const lineStart = lyricAST.getLineStart(i)
        if (i > 0 && (lineStart < lyricAST.getLineStart(i - 1) || lineStart === 0)) {
          this.$alert(`第 ${i + 1} 行开始时间不正确，请检查`, '提示', { type: 'warning' })
          return false
        }

        for (let j = 1; j < lineLen; j++) {
          const wordStart = lyricAST.getWordStart(i, j)
          if (wordStart < lyricAST.getWordStart(i, j - 1) || wordStart === 0) {
            this.$alert(`第 ${i + 1} 行第 ${j + 1} 个字开始时间不正确，请检查`, '提示', { type: 'warning' })
            return false
          }
        }
      }

      return true
    },

    lyricMakeFinish() {
      const { lyricAST, needRoma } = this
      const trc = new Trc()
      const krc = new Krc()
      const lrcx = new Lrcx()
      const lrc = new Lrc()

      trc.readFromAST(lyricAST)
      trc.generateLyric()

      krc.readFromAST(lyricAST)
      krc.generateLyric()

      lrcx.readFromAST(lyricAST)
      lrcx.generateLyric()

      lrc.readFromAST(lyricAST)
      lrc.generateLyric()

      const lyricObj = { trc, krc, lrcx, lrc }

      if (needRoma) {
        const trcRoma = new TrcRoma()
        const krcRoma = new KrcRoma()
        const lrcxRoma = new LrcxRoma()
        const lrcRoma = new LrcRoma()

        const ast = cloneDeep(lyricAST)
        const tlsAST = ast.tlsAST
        // 音译歌词没有时间戳等信息，把文字重新赋值回主 ast
        for (let i = 0; i < ast.ASTList.length; i++) {
          const line = ast.ASTList[i]
          const tlsLine = tlsAST.ASTList[i]
          for (let j = 0; j < line.length; j++) {
            const wordObj = line[j]
            wordObj.word = tlsLine[j].word
            ast.sentencesWordsList[i][j] = wordObj.word
          }
        }

        trcRoma.readFromAST(ast)
        trcRoma.generateLyric()

        krcRoma.readFromAST(ast)
        krcRoma.generateLyric()

        lrcxRoma.readFromAST(ast)
        lrcxRoma.generateLyric()

        lrcRoma.readFromAST(ast)
        lrcRoma.generateLyric()

        lyricObj.trcRoma = trcRoma
        lyricObj.krcRoma = krcRoma
        lyricObj.lrcxRoma = lrcxRoma
        lyricObj.lrcRoma = lrcRoma
      }

      this.$emit('finish', lyricObj)
    },

    handlePress(evt) {
      if (this.step === 2) {
        evt.stopPropagation()
        evt.preventDefault()
        this.calTimeing = true

        switch (evt.code) {
          case KEY_RIGHT:
            this.right()
            break
          case KEY_LEFT:
            this.left()
            break
          case KEY_UP:
            this.up()
            break
          case KEY_DOWN:
            this.down()
            break
          case KEY_SPACE:
            this.space()
            break
          default:
            break
        }
      }
    },

    handleLineModify(lineIndex) {
      const { lyricAST, $refs, needRoma, romaTlsMap, romaType } = this
      const lineText = lyricAST.getLineText(lineIndex)
      const lineStart = lyricAST.getLineStart(lineIndex)
      this.pause()

      this.$prompt('', '修改歌词', {
        inputValue: lineText,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPattern: /^[^\s].*$/,
        inputErrorMessage: '前后不能有空格，至少包含一个可见字符'
      }).then(({ value }) => {
        const lineText = value
        const lineWordList = lyricAST.getWordList(lineText)
        const lineAST = lyricAST.getLineAST(lineText)

        lyricAST.replaceLine(lineIndex, lineWordList, lineAST)
        if (needRoma) {
          const translator = romaTlsMap[romaType]
          const tlsWordList = lineWordList.map(word => {
            return translator.translate(word)
          })
          lyricAST.tlsAST.setLineWords(lineIndex, tlsWordList)
        }
        lyricAST.setLineStart(lineIndex, lineStart)
        lyricAST.setWordStart(lineIndex, 0, lineStart)
        const lineLen = lyricAST.getLineLength(lineIndex)

        this.currentLineLength = lineLen
        $refs.player.seek(lineStart) // 设置播放进度后会根据时间自动重置行数
        this.currentMarkWord = 0
        this.play()
        $refs.lyricMaker.focus()
      }).catch(error => {
        console.error(error)
        this.$refs.lyricMaker.focus()
      })
    },

    /**
     * 删除当前行
     * @param {Number} lineIndex 当前行号
     */
    handleLineDelete(lineIndex) {
      const { lyricAST, needRoma, $refs } = this
      this.$alert('确定删除当前行吗？', '提示', {
        type: 'warning'
      }).then(_ => {
        lyricAST.deleteLine(lineIndex)
        if (needRoma) {
          lyricAST.tlsAST.deleteLine(lineIndex)
        }

        const prevLine = lineIndex > 0 ? lineIndex - 1 : 0
        this.currentMarkLine = prevLine
        this.currentMarkWord = 0
        this.currentLineLength = lyricAST.getLineLength(prevLine)
        this.lineCount = lyricAST.getASTLength()
        const startTime = lyricAST.getLineStart(prevLine)
        $refs.player.seek(startTime)
      }).catch(_ => {
        // 为了不报错
      })
    },

    handleTlsLineModify(lineIndex) {
      const { lyricAST } = this
      const lineWords = lyricAST.getLineWords(lineIndex)
      this.pause()
      this.originTextList = lineWords
      this.tlsTextList = lyricAST.tlsAST.getLineWords(lineIndex).map(text => text.trim())
      this.editTlsLine = lineIndex

      this.showRomaEdit = true
    },
    handleCancelTlsEdit() {
      this.showRomaEdit = false
    },
    handleTlsInput(wordIndex, evt) {
      this.tlsTextList[wordIndex] = evt.target.childNodes[0].data.trim()
    },
    handleTlsConfirm() {
      const { editTlsLine, tlsTextList, lyricAST } = this

      // for (let i = 0; i < tlsTextList.length; i++) {
      //   const text = tlsTextList[i]
      //   if (!/^[a-zA-Z ]*$/g.test(text) || text.trim() === '') {
      //     return this.$alert(`第 ${i + 1} 个字输入了不规范内容，请检查`)
      //   }
      // }

      // 修改后的音译回写 AST
      lyricAST.tlsAST.setLineWords(editTlsLine, tlsTextList)
      this.handleCancelTlsEdit()
      this.play()
    },

    reset() {
      const { lyricAST, $refs } = this
      this.currentMarkLine = 0
      this.currentMarkWord = 0
      this.currentLineLength = lyricAST.getLineLength(0)
      this.lineCount = lyricAST.getASTLength()
      this.makeFinish = false
      $refs.player.seek(0)
    },

    right() {
      const { currentMarkLine, currentMarkWord, currentLineLength, currentPos, lyricAST, lineCount } = this

      // 处理当前字，设置字时长
      const wordDuration = currentPos - lyricAST.getWordStart(currentMarkLine, currentMarkWord)
      // 当时间差为 0,可能是电脑卡了一下, 把当前时间戳+1 处理, 这样算出来每个字的时长不会是 0
      const resolvedCurrentPos = wordDuration > 0 ? currentPos : currentPos + 1
      if (!this.alreadyCal) {
        lyricAST.setWordDuration(currentMarkLine, currentMarkWord, wordDuration || 1)
      }

      if (currentMarkWord === (currentLineLength - 1)) {
        // 最后一个子，设置当前行时长
        const nextLine = currentMarkLine + 1
        const lineDuration = resolvedCurrentPos - lyricAST.getLineStart(currentMarkLine)
        if (!this.alreadyCal) {
          lyricAST.setLineDuration(currentMarkLine, lineDuration)
        }
        this.alreadyCal = false

        if (lineCount === (currentMarkLine + 1)) {
          // 最后一行最后一字了
          this.makeFinish = true
          this.alreadyCal = true
          return
        }

        // 换行，设置下一行的行开始和第一个字开始时间点
        this.currentMarkLine++
        this.currentMarkWord = 0
        lyricAST.setWordStart(nextLine, 0, resolvedCurrentPos)
        lyricAST.setLineStart(nextLine, resolvedCurrentPos)
        this.currentLineLength = lyricAST.getLineLength(nextLine)
        return
      }

      // 处理下一个字
      this.currentMarkWord++
      lyricAST.setWordStart(currentMarkLine, currentMarkWord + 1, resolvedCurrentPos)
      this.alreadyCal = false
    },

    left() {
      const { currentMarkLine, currentMarkWord, lyricAST, $refs } = this
      const prevWord = currentMarkWord - 1
      this.alreadyCal = false
      this.makeFinish = false

      // 第一个字，回到上一行最后一个字
      if (currentMarkWord === 0) {
        if (currentMarkLine === 0) return // 第一行第一个字

        const prevLine = currentMarkLine - 1
        const prevLength = lyricAST.getLineLength(prevLine)
        const prevStart = lyricAST.getWordStart(prevLine, prevLength - 1)

        $refs.player.seek(prevStart) // 设置播放进度
        this.currentLineLength = prevLength
        this.currentMarkWord = prevLength - 1
        this.currentMarkLine--
        return
      }

      // 回到上个字
      const prevStart = lyricAST.getWordStart(currentMarkLine, prevWord)
      $refs.player.seek(prevStart)
      this.currentMarkWord--
    },

    up() {
      const { currentMarkLine, lyricAST, $refs } = this
      this.alreadyCal = false
      this.makeFinish = false
      if (currentMarkLine === 0) return

      const prevLine = currentMarkLine - 1
      const startTime = lyricAST.getLineStart(prevLine)
      const prevLength = lyricAST.getLineLength(prevLine)

      this.currentLineLength = prevLength
      $refs.player.seek(startTime) // 设置播放进度后会根据时间自动重置行数
      this.currentMarkWord = 0
      this.currentMarkLine--
    },

    /**
     * 当前字在当前时间点结束
     */
    down() {
      const { currentMarkLine, currentMarkWord, currentPos, currentLineLength, lineCount, lyricAST } = this
      const wordDuration = currentPos - lyricAST.getWordStart(currentMarkLine, currentMarkWord)
      lyricAST.setWordDuration(currentMarkLine, currentMarkWord, wordDuration)
      this.alreadyCal = true

      if (currentMarkWord === (currentLineLength - 1)) {
        const lineDuration = currentPos - lyricAST.getLineStart(currentMarkLine)
        lyricAST.setLineDuration(currentMarkLine, lineDuration)

        if (lineCount === (currentMarkLine + 1)) {
          // 最后一行最后一字了
          this.makeFinish = true
        }
      }
    },

    space() {
      const player = this.$refs.player

      if (player.playing) {
        player.pause()
      } else {
        player.play()
      }
    },

    currentPlayPosition(second) {
      // console.log(second)
      this.currentPos = second * 1000
    },

    /**
     * 人为拖动进度条的回调，调整歌词的展示
     * 1、如果往前拖动，回滚到时间戳对应的行和字
     * 2、如果往后拖动，不做任何操作
     * @param {Number} value 毫秒
     */
    currentSliderChange(value) {
      if (value === 0) {
        this.currentMarkLine = 0
        this.currentMarkWord = 0
        return
      }

      const { lyricAST } = this
      const timeList = lyricAST.getSentencesTimeList()
      const maxLine = lyricAST.getASTLength()
      let lineStart = 0
      let line = -1

      while (lineStart <= value) { // 获取鼠标放开时播放时间所在的行
        if (line + 2 === maxLine) return // 遍历到最后一行，时间还是比当前时间小，直接退出

        line++
        lineStart = timeList[line + 1][0] // 下一行的开始时间
      }

      // 获取完行，下一步定位字
      const lineLen = lyricAST.getLineLength(line)
      let i = 0
      // 获取当前播放的字
      for (; i < lineLen; i++) {
        const wordStart = lyricAST.getWordStart(line, i + 1)
        if (wordStart > value) {
          break
        }
      }

      this.currentMarkLine = line
      this.currentMarkWord = i
    },

    /**
     * 预览时正常播放进度改成回调
     * 根据时间确定当前在哪行那个字
     */
    previewPlayPostion(second) {
      const { currentMarkLine, currentMarkWord, lineCount, currentLineLength, lyricAST } = this
      const currentMill = second * 1000
      this.currentPos = currentMill

      // 歌词已经到最后
      if ((currentMarkLine === lineCount - 1) && currentMarkWord === currentLineLength - 1) {
        return
      }

      // console.log(currentMarkLine)
      // console.log(currentMarkWord)
      const wordEnd = lyricAST.getWordEnd(currentMarkLine, currentMarkWord)
      if (currentMill > wordEnd) {
        if (currentMarkWord === currentLineLength - 1) {
          if (currentMarkLine === lyricAST.getASTLength() - 1) return // 最后一行最后一字

          const nextLineWordStart = lyricAST.getWordStart(currentMarkLine + 1, 0)
          if (currentMill < nextLineWordStart) return // 还没到下一行，不跳
          // 跳到下一行第一个字
          this.currentMarkWord = 0
          this.currentMarkLine++
          this.currentLineLength = lyricAST.getLineLength(currentMarkLine + 1)
          return
        }

        this.currentMarkWord++
      }
    },
    /**
     * @param {Number} value 毫秒
     */
    // previewSliderChange(value) {
    //   if (value === 0) {
    //     this.currentMarkLine = 0
    //     this.currentMarkWord = 0
    //   }
    //   const { currentMarkLine, currentMarkWord, lyricAST } = this
    //   const timeList = lyricAST.getSentencesTimeList()
    //   let lineStart = 0
    //   let line = -1

    //   while (lineStart <= value) { // 循环获取 行开始时间 大于当前时间的行下标
    //     line++
    //     lineStart = timeList[line + 1][0]

    //     if (lineStart === 0 && line !== 0) {
    //       return
    //     }
    //   }

    //   const lineLen = lyricAST.getLineLength(line)
    //   let i = 0
    //   for (; i < lineLen; i++) {
    //     const wordStart = lyricAST.getWordStart(line + 1, i)
    //     if (wordStart > value) {
    //       break
    //     }
    //   }

    //   this.currentMarkLine = line
    //   this.currentMarkWord = i
    // },

    play() {
      this.$refs.player.play()
    },

    pause() {
      this.$refs.player.pause()
    },
  }
}
</script>

<style lang="scss">
@import "@/styles/variables.scss";

.lyric-maker {
  position: relative;

  &:focus {
    // outline: 1px solid red;
  }

  .btn {
    margin: 16px 0 0 0;
  }

  .info-block {
    padding-top: 16px;
    padding-left: 0;

    .replace-input {
      width: 170px;
    }

    /deep/ .el-form-item {
      margin-bottom: 0;
    }
    /deep/ .el-form-item__label {
      color: $detail;
    }
  }

  .maker-wrapper {
    position: relative;
    overflow: hidden;
    margin-top: 8px;
    border: 1px solid #ccc;
    width: 650px;
    height: 400px;

    .info-panel {
      position: absolute;
      right: 0;
      bottom: 0;
    }

    .content {
      position: absolute;
      top: 0;
      width: 100%;
      transition: top .5s;

      &.play-mod {
        text-align: center;

        .line__current {
          &.marked {
            // border-width: 2px;
            color: $success;
          }
        }
        .line:not(.line__current) {
          .word {
            color: #000;
          }
        }

        .word {
          border: none;
          color: #000;

          &.marked {
            // border-width: 2px;
            color: $success;
          }
        }
        .text {
          border: none;
        }
      }
    }

    .line {
      position: relative;
      margin: 2px 0;
      padding: 4px;
      height: 40px;
      font-size: 16px;
      border: 1px solid transparent;

      &.line__current {
        // border-color: $primary;
        .text {

          border-left-color: transparent;
        }
        .tool {
          visibility: visible;
        }
      }

      &.marked {
        .word {
          border-color: $success;
        }
      }

      .tool {
        position: absolute;
        visibility: hidden;
        /*left: 490px;*/
        top: 50%;
        width: 150px;
        // right: 10px;
        vertical-align: middle;
        transform: translateY(-11px);
      }

      .el-button {
        padding: 4px;
        font-size: 12px;
      }
    }
    .finish {
      position: absolute;
      color: $success;
      font-style: italic;
    }
    .text {
      display: inline-block;
      // max-width: 550px;
      white-space: nowrap;
      padding: 4px 0;
    }
    .word {
      position: relative;
      line-height: 22px;
      padding: 0 4px;
      border: 2px solid rgba(204, 204, 204, 0.6);
      border-left: none;
      // padding-right: 6px;

      .tls-word {
        position: absolute;
        bottom: -20px;
        left: 1px;
        font-size: 12px;
      }

      &:first-child {
        border-left: 2px solid rgba(204, 204, 204, 0.6);
      }

      &.marked {
        // border-width: 2px;
        border-color: $success;
      }
      &.current {
        border-color: $primary;
      }
      &.error {
        /*border: 2px solid;*/
        border-color: $danger !important;
      }

    }
  }
}
.tls-edit-line {
  width: 100%;
  padding: 24px 0 16px;
  white-space: nowrap;
  overflow-x: auto;

  .tls-edit-word {
    position: relative;
    display: inline-block;
    padding: 2px 6px;
    border: 1px solid #DCDFE6;
    border-radius: 4px;
    color: #606266;
    outline: 0;

    &:hover {
      border-color: #C0C4CC;
    }
    &:focus {
      border-color: #409EFF;
    }

    &+.tls-edit-word {
      margin-left: 4px;
    }

    .tls-edit-orig {
      position: absolute;
      top: -18px;
      left: 50%;
      transform: translateX(-50%);
    }
  }

}
</style>
